import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useItemQuery } from '../hooks/useItemQuery'
import { Card } from '../components/card/Card'
import { Button } from '../components/common/Button'
import { EmptyState, ErrorBox, Loading } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Tabs } from '../components/common/Tabs'
import { Tree, toTree } from '../components/common/Tree'
import type { TreeNode } from '../components/common/Tree'
import s from './IndustriesPage.module.scss'

/** Dasselbe Recht wie die Instrumentenliste — der Baum ist deren Stammdatum. */
const PERMISSION = 'instrument:read'

/**
 * Ein Reiter je Norm.
 *
 * Alle fest verdrahtet und nicht aus den Daten gezogen: Eine Norm ohne
 * Einträge SOLL sichtbar bleiben. Zöge man die Reiter aus dem Bestand, blieben
 * heute nur GICS übrig — und niemand sähe, dass es die anderen gibt und
 * dass sie leer sind. Genau das ist die Auskunft, um die es gerade geht.
 *
 * `hint` erklärt, wonach die Norm ordnet. Zwei Normen können denselben Namen
 * für Verschiedenes benutzen: „Energie" ist bei GICS ein Geschäftszweck und bei
 * SIC ein Eintrag im Register.
 *
 * UNSERE EIGENE STEHT VORN, weil sie die einzige ist, die wir zuordnen. Die
 * vier gekauften schneiden nach Produkt oder Registereintrag; sie beantworten
 * nicht, was sich miteinander bewegt.
 */
const SCHEMES = [
  { key: 'IW', label: 'Instrument Watcher', hint: 'Unsere eigene Gliederung — zwei Ebenen, nach Kurstreiber geschnitten. Als einzige erlaubt sie einem Titel mehrere Fächer mit Anteilen: Amazon steht im Onlinehandel UND in der Cloud-Infrastruktur.' },
  { key: 'GICS', label: 'GICS', hint: 'Womit ein Unternehmen sein Geld verdient. Elf Sektoren; die drei Ebenen darunter sind lizenziert und deshalb nicht enthalten.' },
  { key: 'SIC', label: 'SIC', hint: 'Als was ein Unternehmen sich bei der US-Börsenaufsicht angemeldet hat. Vierstellig, über die CIK erreichbar.' },
  { key: 'NAICS', label: 'NAICS', hint: 'Der nordamerikanische Nachfolger von SIC, sechsstellig. Ohne Zuordnung: Börsennotierte Firmen melden keinen NAICS, jede Angabe wäre aus dem SIC abgeleitet.' },
  { key: 'NACE', label: 'NACE', hint: 'Die europäische Entsprechung, geführt von Eurostat. Ohne Zuordnung: Den Code führen die Handelsregister, nicht die Marktdatenanbieter.' },
]

/** Der Buchstabe aus der Datenbank, in Worten — wie in der Instrumentenliste. */
const TYPES: Record<string, string> = { S: 'Aktien', E: 'ETFs', I: 'Indizes' }

const zahl = new Intl.NumberFormat('de-DE')

/** Kommagetrennt in der Adresse, als Array in der Komponente. */
function alsListe(wert: string): string[] {
  return wert ? wert.split(',').filter(Boolean) : []
}

export function IndustriesPage() {
  const allowed = useHasPermission(PERMISSION)
  const navigate = useNavigate()

  // Norm und offene Knoten stehen in der Adresse: teilbar, überlebt das
  // Neuladen, und die Zurück-Taste geht einen Schritt zurück statt von der
  // Seite weg.
  const list = useListParams({
    filters: { scheme: 'IW', open: '' },
    sort: 'code',
  })

  const scheme = list.filters.scheme || 'IW'

  // `useCallback` ist hier nicht schmückend: Die Ladefunktion steht in den
  // Abhängigkeiten des Effekts. Eine bei jedem Rendern neu gebildete Funktion
  // löst ihn erneut aus, das Ergebnis setzt den Zustand, das rendert neu — und
  // der Abruf läuft endlos, bis der Server abriegelt.
  const laden = useCallback(
    (signal: AbortSignal) => api.industryTree(scheme, signal),
    [scheme])
  const { item, loading, error, denied } = useItemQuery(laden, allowed)

  const eintraege = useMemo(() => item?.items ?? [], [item])
  const offen = alsListe(list.filters.open)

  // Aus der flachen Liste wird der Baum. Die Verschachtelung entsteht hier,
  // aus derselben Elternangabe, die auch in der Datenbank steht.
  const knoten = useMemo<TreeNode[]>(
    () => toTree(
      eintraege,
      (i) => i.code,
      (i) => i.parent_code,
      (i, kinder) => ({
        id: i.code,
        label: i.name,
        hint: i.code,
        value: zahl.format(i.total_count ?? 0),
        children: kinder,
        // Der Pfeil springt in die Instrumentenliste mit gesetztem Filter.
        // `exchange=-` hebt die Favoritenvorbelegung dort auf: Sonst zeigte
        // die Liste weniger Titel, als hier stehen, und die beiden Zahlen
        // widersprächen einander.
        action: (
          <button type="button" className={s.sprung}
                  title={`${i.name} in der Instrumentenliste`}
                  aria-label={`${i.name} in der Instrumentenliste zeigen`}
                  onClick={() => navigate(
                    `/instruments?industry=${i.scheme}:${i.code}&exchange=-`)}>
            →
          </button>
        ),
      })),
    [eintraege, navigate])

  // Wer Kinder hat, lässt sich aufklappen. Aus den ROHDATEN gelesen und
  // nicht aus dem gebauten Baum: Dort müsste man rekursiv absteigen, hier
  // genügt ein Durchgang über die Elternangaben.
  const aufklappbar = useMemo(
    () => [...new Set(eintraege.map((i) => i.parent_code).filter(
      (c): c is string => Boolean(c)))],
    [eintraege])

  //: `open=*` heißt „alles offen" — statt alle Codes aufzuzählen.
  //:
  //: Gemessen: Die Codes von NAICS ergäben einen Parameter von 6.129
  //: Zeichen. Das ist keine teilbare Adresse mehr, und beim Neuladen ginge
  //: sie so an den Server. Der Stern sagt dasselbe in einem Zeichen — und
  //: bleibt richtig, wenn der Baum wächst.
  const ALLE = '*'
  const offeneKnoten = useMemo(
    () => (list.filters.open === ALLE ? aufklappbar : offen),
    [list.filters.open, aufklappbar, offen])

  const umschalten = useCallback((id: string) => {
    // Aus `*` wird beim ersten Handgriff eine echte Liste: Wer einen Knoten
    // zuklappt, meint genau diesen — nicht „alles offen minus einer", was
    // der Stern nicht ausdrücken kann.
    const jetzt = list.filters.open === ALLE ? aufklappbar
                                             : alsListe(list.filters.open)
    const neu = jetzt.includes(id)
      ? jetzt.filter((k) => k !== id)
      : [...jetzt, id]
    list.setFilter('open', neu.join(','))
  }, [list, aufklappbar])

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Branchen" />
  }

  const norm = SCHEMES.find((n) => n.key === scheme) ?? SCHEMES[0]

  return (
    <Card span={12} flush>
      <div className={s.kopf}>
        <Tabs items={SCHEMES} active={scheme}
              // Beide Werte in EINEM Aufruf. Zwei `setFilter` hintereinander
              // gehen verloren: Sie lesen dasselbe `params` dieses Renderns,
              // und der zweite nimmt die Änderung des ersten wieder weg — der
              // Reiter sprang dann zurück auf die alte Norm.
              //
              // Die offenen Knoten fallen mit, weil dieselben Codes in einer
              // anderen Norm etwas anderes bedeuten.
              onChange={(key) => list.setFilters({ scheme: key, open: '' })}
              panelId="branchen-inhalt"
              aria-label="Klassifikation" />

        {/*
          Zwei Knöpfe, nicht einer, der umschaltet: Ein Umschalter müsste
          einen Zustand behaupten („ist gerade alles offen?"), den es bei
          teilweise geöffneten Bäumen nicht gibt. Zwei Knöpfe sagen, was
          passiert, und jeder ist abgeblendet, wenn er nichts bewirkt.

          Bei NAICS öffnet der linke 2.105 Knoten auf einmal. Das ist
          gewollt — wer den ganzen Baum sehen will, klickt sich sonst durch
          fünf Ebenen. Die Anzahl steht am Knopf, damit niemand von der
          Länge überrascht wird.
        */}
        <Button onClick={() => list.setFilter('open', ALLE)}
                disabled={aufklappbar.length === 0
                          || offeneKnoten.length === aufklappbar.length}>
          alle aufklappen
          {aufklappbar.length > 0 && ` (${zahl.format(aufklappbar.length)})`}
        </Button>

        <Button onClick={() => list.setFilter('open', '')}
                disabled={offeneKnoten.length === 0}>
          alle zuklappen
        </Button>
      </div>

      {/*
        Was die Norm ordnet, steht unter den Reitern und nicht in einem
        Fragezeichen: Es ist die Auskunft, die den ganzen Reiter erklärt, und
        ohne sie stehen vier Abkürzungen da, die alle irgendwie „Branche"
        heißen.
      */}
      <p className={s.erklaerung}>{norm.hint}</p>

      {error && <ErrorBox>{error}</ErrorBox>}

      <div id="branchen-inhalt" role="tabpanel" className={s.inhalt}>
        {loading && item === null ? <Loading />
          : eintraege.length === 0 ? (
            <EmptyState title={`keine Einträge für ${scheme}`}>
              Für diese Norm gibt es keine frei verfügbare Quelle. Die Spalte
              steht in der Datenbank bereit und bleibt vorerst leer.
            </EmptyState>
          ) : (
            <Tree nodes={knoten} expanded={offeneKnoten} onToggle={umschalten}
                  columns={{ label: 'Branche', hint: 'Code', value: 'Titel' }}
                  aria-label={`Branchen nach ${scheme}, mit der Anzahl der Instrumente je Branche`} />
          )}
      </div>

      {/*
        Die Bilanz beantwortet die Frage, die beim Ansehen des Baums entsteht:
        Warum ergeben die Zahlen zusammen nicht den Bestand?

        Aufgeschlüsselt nach Art, weil „nicht eingeordnet" zweierlei heißt.
        Ein ETF ohne Sektor ist RICHTIG — ein Fonds hält Titel, die einen
        Geschäftszweck haben, aber keinen eigenen. Eine Aktie ohne Sektor ist
        eine LÜCKE. Als eine Zahl ließe sich das nicht unterscheiden.
      */}
      {item && (
        <div className={s.bilanz}>
          <span>eingeordnet</span>
          <b>{zahl.format(item.classified)}</b>
          {Object.entries(item.unclassified_by_type)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([art, anzahl]) => (
              <span key={art} className={s.posten}>
                ohne&nbsp;Einordnung, {TYPES[art] ?? 'ohne Art'}
                <b className={s.gedaempft}>{zahl.format(anzahl)}</b>
              </span>
            ))}
          <span className={s.posten}>
            Bestand
            <b>{zahl.format(item.classified + item.unclassified)}</b>
          </span>
        </div>
      )}
    </Card>
  )
}
